if (!customElements.get('kmdro-article-toc')) {
  customElements.define('kmdro-article-toc', class extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.build(), { once: true });
      } else {
        this.build();
      }
    }

    build() {
      if (this.dataset.ready || !this.isConnected) return;
      const body = this.closest('[data-article-body]');
      const list = this.querySelector('[data-toc-list]');
      if (!body || !list) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const desktop = matchMedia('(min-width: 1100px)');
      const details = this.querySelector('details');
      const setMode = () => { details.open = desktop.matches; };
      setMode();
      desktop.addEventListener('change', setMode, { signal });
      const headings = [...body.querySelectorAll('h2, h3')].filter(heading =>
        heading.textContent.trim() && !heading.closest('nav, kmdro-article-toc, [hidden], [aria-hidden="true"]')
      );
      if (headings.length < 2) return;
      let parentItem;
      const targets = new Map();
      headings.forEach((heading, index) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const slug = heading.textContent.trim().toLowerCase().normalize('NFKC')
            .replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').slice(0, 70);
          const base = `article-section-${slug || index + 1}`;
          let id = base;
          let suffix = 2;
          while (document.getElementById(id)) id = `${base}-${suffix++}`;
          heading.id = id;
        }
        heading.classList.add('kmdro-toc-target');
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.textContent = heading.textContent.trim();
        link.setAttribute('href', `#${encodeURIComponent(heading.id)}`);
        targets.set(link, heading);
        item.append(link);
        if (heading.tagName === 'H3' && parentItem) {
          let children = parentItem.querySelector('ol');
          if (!children) {
            children = document.createElement('ol');
            parentItem.append(children);
          }
          children.append(item);
        } else {
          list.append(item);
          if (heading.tagName === 'H2') parentItem = item;
        }
      });
      const reveal = heading => {
        for (let ancestor = heading.parentElement; ancestor && ancestor !== body; ancestor = ancestor.parentElement) {
          if (ancestor.tagName === 'DETAILS') ancestor.open = true;
        }
      };
      this.addEventListener('click', event => {
        const link = event.target.closest('a');
        const heading = targets.get(link);
        if (!heading || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        reveal(heading);
        if (!desktop.matches) details.open = false;
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && details.open) {
          details.open = false;
          this.querySelector('summary').focus();
        }
      }, { signal });
      body.querySelectorAll('.km-toc').forEach(existing => { existing.hidden = true; });
      body.classList.add('kmdro-has-toc');
      this.dataset.ready = 'true';
      this.hidden = false;
      const updateCurrent = () => {
        this.frame = null;
        const offset = parseFloat(getComputedStyle(headings[0]).scrollMarginTop) || 124;
        const visible = headings.filter(heading => heading.getClientRects().length);
        const current = visible.filter(heading => heading.getBoundingClientRect().top <= offset + 8).pop() || visible[0];
        targets.forEach((heading, link) => {
          if (heading === current) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };
      const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(updateCurrent); };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      updateCurrent();
      let initialId;
      try { initialId = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      const initial = headings.find(heading => heading.id === initialId);
      if (initial) {
        reveal(initial);
        requestAnimationFrame(() => initial.scrollIntoView());
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.querySelector('[data-toc-list]')?.replaceChildren();
      delete this.dataset.ready;
    }
  });
}
